import _ from 'lodash';
import React, {FC, useState} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {useUnmountCancelToken} from '../../../../srcCommon/helpers/hookHelpers';
import {TagTemplate} from '../../../components/common/dropdowns/tags/tagEditorBody';
import {buildInboxSearchUrl} from '../../../helpers/models/inboxHelpers';
import {isTeammateNamespace, isTeamNamespace} from '../../../helpers/models/namespaceHelpers';
import {ResolveDispatchProps} from '../../../helpers/reduxHelpers';
import {
  AppParametricSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../helpers/reselectHelpers';
import {HighlightTypesEnum} from '../../../models/api/highlightBaseModel';
import {InboxModel} from '../../../models/api/inboxModel';
import {PermissionsEnum} from '../../../models/api/roleModel';
import {ApiSearchInboxes} from '../../../models/api/searchInboxModel';
import {ApiTag, TagModel} from '../../../models/api/tagModel';
import {buildSettingsPath} from '../../../models/url/settingsUrlHelpers';
import {SettingsResourcePagesEnum, SettingsTagsUrl} from '../../../models/url/settingsUrlModel';
import {fetchApiAsync} from '../../../services/http/http';
import {
  InterfaceEventsEnum,
  SettingsTrackingTypesEnum,
  trackInterfaceEvent,
} from '../../../services/tracking';
import {AppState} from '../../../store/appState';
import {getHasPermission} from '../../../store/data/access/accessSelectors';
import {getCompanyUrl} from '../../../store/data/company/companySelectors';
import {makeGetInboxesByNamespace} from '../../../store/data/inboxes/inboxesSelectors';
import {
  toggleHideTagAction,
  updateShouldShowNewTagInCellsAction,
} from '../../../store/data/preferences/preferencesActions';
import {
  getShouldShowNewTagInCells,
  getShouldShowNewTagInWorkspaces,
  getVisibleCustomViewIds,
} from '../../../store/data/preferences/preferencesSelectors';
import {pushTagUpdatedAction} from '../../../store/data/tags/pushTagActions';
import {createTagAction, saveTagAction} from '../../../store/data/tags/saveTagActions';
import {getTags} from '../../../store/data/tags/tagsSelectors';
import {getFlatActiveOrPendingTeammates, getUserId} from '../../../store/data/teammates/teammatesSelectors';
import {
  hideWorkspaceTagAsyncAction,
  showWorkspaceTagAsyncAction,
} from '../../../store/data/workspace/workspaceActions';
import {navigateToLinkAction} from '../../../store/stateless/navigationActions';
import {ArchiveTagDialogContainer} from '../archiveTagDialogContainer';
import {DeleteTagDialogContainer} from '../deleteTagDialogContainer';
import {useSettingsTagsContext} from '../settingsTagsContext';
import {SettingsSingleTag} from './settingsSingleTag';

/*
 * Props.
 */

interface SettingsSingleTagContainerProps {
  tagsUrl: SettingsTagsUrl;
  openParentTag: (tagId?: number) => void;
  onCancelClick: Handler;
  isEditing?: boolean;
}

/*
 * Selectors.
 */

type MaybeTagSelector = AppParametricSelector<SettingsSingleTagContainerProps, TagModel | undefined>;

function getTagUrls(_state: AppState, props: SettingsSingleTagContainerProps) {
  return props.tagsUrl;
}

function makeGetTag(): MaybeTagSelector {
  return createSelector([getTagUrls, getTags], (tagsUrl, tags) => {
    const {tagId, resourcePage} = tagsUrl;

    if (resourcePage === SettingsResourcePagesEnum.NEW) {
      return undefined;
    }

    return tagId ? tags[tagId] : undefined;
  });
}

function makeGetParentTag(getTag: MaybeTagSelector) {
  return createSelector([getTag, getTags, getTagUrls], (tag, tags, tagsUrl) => {
    const isNewTag = tagsUrl.resourcePage === SettingsResourcePagesEnum.NEW;

    if (isNewTag && tagsUrl.tagId) {
      return tags[tagsUrl.tagId];
    }

    if (!tag || !tag.parentTagId) {
      return undefined;
    }

    return tags[tag.parentTagId];
  });
}

function getNamespace(_state: AppState, props: SettingsSingleTagContainerProps) {
  return props.tagsUrl.namespace;
}

function makeGetShouldShowTeammatePinFeature(getTag: MaybeTagSelector) {
  return createSelector([getTag, getNamespace, getHasPermission], (tag, namespace, hasPermission) => {
    // Do we have a tag we're editing? Then don't show the feature
    if (tag) {
      return false;
    }

    // If we're not in a team namespace, hide the feature.
    if (!isTeamNamespace(namespace)) {
      return false;
    }

    return hasPermission(namespace, PermissionsEnum.EDIT_TEAMMATE);
  });
}

function makeGetInitialShowInWorkspaceValue(getTag: MaybeTagSelector) {
  return createSelector(
    [getShouldShowNewTagInWorkspaces, getVisibleCustomViewIds, getTag],
    (shouldShowNewTagInWorkspaces, visibleCustomViewIds, tag) =>
      tag ? visibleCustomViewIds.includes(tag.id) : shouldShowNewTagInWorkspaces,
  );
}

function makeGetInitialShowInCellsValue(getTag: MaybeTagSelector) {
  return createSelector([getShouldShowNewTagInCells, getTag], (shouldShowNewTagInCells, tag) =>
    tag ? tag.showInCells : shouldShowNewTagInCells,
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<SettingsSingleTagContainerProps>()(() => {
  const getTag = makeGetTag();
  return {
    tag: getTag,
    namespace: getNamespace,
    parentTag: makeGetParentTag(getTag),
    userId: getUserId,
    teammates: getFlatActiveOrPendingTeammates,
    shouldShowTeammatePinFeature: makeGetShouldShowTeammatePinFeature(getTag),
    initialShowInWorkspaceValue: makeGetInitialShowInWorkspaceValue(getTag),
    initialShowInCellsValue: makeGetInitialShowInCellsValue(getTag),
    companyUrl: getCompanyUrl,
    inboxes: makeGetInboxesByNamespace(getNamespace),
    visibleCustomViewIds: getVisibleCustomViewIds,
  };
});

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  updateShouldShowNewTagInCells: updateShouldShowNewTagInCellsAction,
  toggleHideTag: toggleHideTagAction,
  hideWorkspaceTag: hideWorkspaceTagAsyncAction,
  showWorkspaceTag: showWorkspaceTagAsyncAction,
  createTag: createTagAction,
  saveTag: saveTagAction,
  pushTagUpdated: pushTagUpdatedAction,
  navigateToLink: navigateToLinkAction,
}));

interface SettingsSingleTagContainerComponentProps
  extends SettingsSingleTagContainerProps,
    ResolveStateProps<typeof mapStateToProps>,
    ResolveDispatchProps<typeof mapDispatchToProps> {}

const fetchInboxesPageSize = 50;

const SettingsSingleTagContainerComponent: FC<SettingsSingleTagContainerComponentProps> = (props) => {
  const {clearTagsCache, singleTagCreationEntryPoint} = useSettingsTagsContext();
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [isArchiveConfirmationOpen, setIsArchiveConfirmationOpen] = useState(false);
  const [inboxes, setInboxes] = useState<ReadonlyArray<InboxModel>>([]);
  const cancelToken = useUnmountCancelToken();

  const {tag} = props;

  const fetchAllInboxes = async (nextPageIndex = 0) => {
    // Build our search url
    const url = buildInboxSearchUrl(props.companyUrl, '', {
      namespace: props.tagsUrl.namespace,
      offset: nextPageIndex * fetchInboxesPageSize,
      limit: fetchInboxesPageSize,
      shouldIncludePrivate: false,
    });
    // Fetch inboxes
    const fetchedInboxes = await fetchApiAsync(ApiSearchInboxes, url, {cancelToken});
    // Check whether we need to load more of them
    const canLoadMore = fetchedInboxes.inboxes.length === fetchedInboxes.limit;
    // Add our newly fetched inboxes to the state
    const newInboxesList = _(inboxes)
      .concat(fetchedInboxes.inboxes)
      .uniqBy((i) => i.id)
      .value();
    setInboxes(newInboxesList);

    // If we need to, go for another round to fetch more inboxes.
    if (canLoadMore) {
      fetchAllInboxes(nextPageIndex + 1);
    }
    // If not, we're done and we add our initial list of inboxes to the ones we fetched
    else {
      setInboxes((prevInboxes) =>
        _(props.inboxes)
          .concat(prevInboxes)
          .uniqBy((i) => i.id)
          .sortBy((i) => i.name.toLowerCase())
          .value(),
      );
    }
  };

  useInitialEffect(() => {
    // If we're in a private namespace, we don't need to fetch anything.
    if (isTeammateNamespace(props.tagsUrl.namespace)) {
      return;
    }

    fetchAllInboxes();
  });

  useInitialEffect(() => {
    if (props.tag || !props.tagsUrl.tagId) {
      return;
    }

    const url = `${props.companyUrl}/tags/${props.tagsUrl.tagId}`;
    fetchApiAsync(ApiTag, url).then((editedTag) => {
      props.pushTagUpdated(editedTag);
    });
  });

  const onSaveClick = async (tagTemplate: TagTemplate) => {
    clearTagsCache();
    if (props.isEditing && props.tag) {
      // If toggling tag visibility in workspace, toggle it in the workspace resource:
      if (tagTemplate.showInWorkspaces) {
        props.showWorkspaceTag(props.tag.id, props.tag.isView, props.tag.namespace);
      } else {
        props.hideWorkspaceTag(props.tag.id, props.tag.isView, props.tag.namespace);
      }
      // If toggling tag visibility in workspace, toggle it in the user's preferences:
      props.toggleHideTag(props.tag.id, [props.tag], {value: tagTemplate.showInWorkspaces});

      props.updateShouldShowNewTagInCells(tagTemplate.showInCells);

      trackInterfaceEvent(
        InterfaceEventsEnum.SETTINGS_TAG_UPDATE,
        makeTagUpdateEventValue(props, tagTemplate),
      );
      return props.saveTag(props.tag.id, {
        newName: tagTemplate.name,
        newParentTags: tagTemplate.parentTags,
        newShowInCells: tagTemplate.showInCells,
        newType: tagTemplate.highlightType,
        newHighlightColor: tagTemplate.highlightColor,
        newHighlightEmoji: tagTemplate.highlightEmoji,
        newInboxIds: tagTemplate.inboxIds,
        newIsVisibleInAllInboxes: tagTemplate.isVisibleInAllInboxes,
      });
    }

    return props.createTag(tagTemplate, (newTag) => {
      trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_TAG_CREATE, {
        settings_subpage: props.tagsUrl.tabPage,
        settings_type: isTeamNamespace(props.tagsUrl.namespace)
          ? SettingsTrackingTypesEnum.TEAM
          : SettingsTrackingTypesEnum.PERSONAL,
        namespace: props.tagsUrl.namespace,
        entry_point: singleTagCreationEntryPoint,
        tag_id: newTag.id,
      });

      if (props.parentTag) {
        trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_TAG_NEST, {
          settings_subpage: props.tagsUrl.tabPage,
          settings_type: isTeamNamespace(props.tagsUrl.namespace)
            ? SettingsTrackingTypesEnum.TEAM
            : SettingsTrackingTypesEnum.PERSONAL,
          namespace: props.tagsUrl.namespace,
          num_of_items: 1,
          nest_type: 'nest_under',
          is_all_selected: false,
          entry_point: singleTagCreationEntryPoint,
        });
      }
    });
  };

  const maybeRenderDeleteConfirmDialog = () => {
    if (!isDeleteConfirmationOpen || !tag) {
      return null;
    }

    return (
      <DeleteTagDialogContainer
        searchQuery={undefined}
        tagsUrl={props.tagsUrl}
        tagsToActOn={[tag]}
        hasAllSelected={false}
        onRequestClose={() => setIsDeleteConfirmationOpen(false)}
        onDelete={() => {
          setIsDeleteConfirmationOpen(false);
          clearTagsCache();
          trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_TAG_DELETE, {
            settings_subpage: props.tagsUrl.tabPage,
            settings_type: isTeamNamespace(props.tagsUrl.namespace)
              ? SettingsTrackingTypesEnum.TEAM
              : SettingsTrackingTypesEnum.PERSONAL,
            namespace: props.tagsUrl.namespace,
            num_of_items: 0,
            is_all_selected: false,
            entry_point: 'edit_tag',
          });
          props.navigateToLink(
            buildSettingsPath({
              ...props.tagsUrl,
              tagId: undefined,
              resourcePage: SettingsResourcePagesEnum.OVERVIEW,
            }),
          );
        }}
      />
    );
  };

  const maybeRenderArchiveConfirmDialog = () => {
    if (!isArchiveConfirmationOpen || !tag) {
      return null;
    }

    return (
      <ArchiveTagDialogContainer
        tagsUrl={props.tagsUrl}
        tagsToActOn={[tag]}
        searchQuery={undefined}
        hasAllSelected={false}
        onRequestClose={() => setIsArchiveConfirmationOpen(false)}
        onArchive={() => {
          setIsArchiveConfirmationOpen(false);
          trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_TAG_ARCHIVE, {
            settings_subpage: props.tagsUrl.tabPage,
            settings_type: isTeamNamespace(props.tagsUrl.namespace)
              ? SettingsTrackingTypesEnum.TEAM
              : SettingsTrackingTypesEnum.PERSONAL,
            namespace: props.tagsUrl.namespace,
            num_of_items: 0,
            is_all_selected: false,
            entry_point: 'edit_tag',
          });
          clearTagsCache();
          props.navigateToLink(
            buildSettingsPath({
              ...props.tagsUrl,
              tagId: undefined,
              resourcePage: SettingsResourcePagesEnum.OVERVIEW,
            }),
          );
        }}
      />
    );
  };

  return (
    <>
      {maybeRenderDeleteConfirmDialog()}
      {maybeRenderArchiveConfirmDialog()}
      <SettingsSingleTag
        inboxes={inboxes}
        openParentTag={props.openParentTag}
        tag={props.tag}
        namespace={props.namespace}
        parentTag={props.parentTag}
        userId={props.userId}
        teammates={props.teammates}
        shouldShowTeammatePinFeature={props.shouldShowTeammatePinFeature}
        isEditing={props.isEditing}
        onSaveClick={onSaveClick}
        onCancelClick={props.onCancelClick}
        onDeleteClick={() => setIsDeleteConfirmationOpen(true)}
        onArchiveClick={() => setIsArchiveConfirmationOpen(true)}
        initialShowInWorkspaceValue={props.initialShowInWorkspaceValue}
        initialShowInCellsValue={props.initialShowInCellsValue}
      />
    </>
  );
};

export const SettingsSingleTagContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(SettingsSingleTagContainerComponent);

function makeTagUpdateEventValue(props: SettingsSingleTagContainerComponentProps, template: TagTemplate) {
  const existingTag = props.tag;

  if (!existingTag) {
    return undefined;
  }

  const existingEmoji = existingTag.type === HighlightTypesEnum.EMOJI && existingTag.highlightEmoji;
  const hasChangedEmoji = existingEmoji !== template.highlightEmoji;
  const existingColor = existingTag.type === HighlightTypesEnum.COLOR && existingTag.highlightColor;
  const hasChangedColor = existingColor !== template.highlightColor;
  const hasChangedParentId =
    template.parentTags && template.parentTags[0] && existingTag.parentTagId !== template.parentTags[0].id;
  const hasChangedWorkspacePinning =
    props.visibleCustomViewIds.includes(existingTag.id) !== template.showInWorkspaces;
  const hasChangedCells = existingTag.showInCells !== template.showInCells;
  const hasChangedInboxes =
    !_.isEqual(existingTag.inboxIds, template.inboxIds) ||
    existingTag.isVisibleInAllInboxes !== template.isVisibleInAllInboxes;

  return {
    settings_type: isTeamNamespace(props.tagsUrl.namespace)
      ? SettingsTrackingTypesEnum.TEAM
      : SettingsTrackingTypesEnum.PERSONAL,
    namespace: props.tagsUrl.namespace,
    settings_subpage: props.tagsUrl.tabPage,
    tag_id: existingTag.id,
    change_type: _.compact([
      (hasChangedEmoji || hasChangedColor) && 'color_emoji',
      hasChangedParentId && 'nest_options',
      hasChangedWorkspacePinning && 'pinning',
      hasChangedCells && 'convo_list_add',
      hasChangedInboxes && 'inbox_availability',
    ]),
  };
}
